import address from 'forms/shared/validators/models/location/models/address';
import { DEFAULT_EARLIEST, DEFAULT_FUTURE_LATEST, DEFAULT_LATEST } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import { hasYesOrNo } from 'models/validate';
import { isPVQForm } from 'helpers/form';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { REQUIRED } from 'constants/errors';
import { civilCourtOutcomeOptions, courtPartyStatus } from 'constants/enums/legalOptions';
import { LegalNonCriminalCourtActionsItemModel, AdditionalOppositionsItemModel } from '../types';

const additionalOppositionsItemModelValidator: ValidateJSModel<AdditionalOppositionsItemModel> = { // PVQ Only

  OppositionPartyName: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator }
      };
    }
    return {};
  },
  OppositionCourtPartyStatus: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: [courtPartyStatus.plaintiff, courtPartyStatus.defendant],
          },
        },
      };
    }
    return {};
  }
};

const legalNonCriminalCourtActionsItemValidator: ValidateJSModel<LegalNonCriminalCourtActionsItemModel> = {

  CivilActionDate: (value, attributes, attributeName, options) => { // SF Only
    const { applicantBirthdate } = options;
    if (isPVQForm(options.formType)) {
      return {};
    }
    return {
      presence: true,
      date: { earliest: applicantBirthdate, latest: DEFAULT_LATEST },
    };
  },
  ApplicantCourtPartyStatus: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: [courtPartyStatus.plaintiff, courtPartyStatus.defendant],
          },
        },
      };
    }
    return {};
  },
  AdditionalOppositions: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        presence: true,
        branchCollection: {
          validator: additionalOppositionsItemModelValidator
        },
      };
    }
    return {};
  },
  OppositionPartyName: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator }
      };
    }
    return {};
  },
  OppositionCourtPartyStatus: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: [courtPartyStatus.plaintiff, courtPartyStatus.defendant],
          },
        },
      };
    }
    return {};
  },

  CourtName: { presence: true, hasValue: true },
  CourtAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options.formType)) {
      return {
        location: {
          requireStreet: value?.inUSA,
          requireState: value?.inUSA,
          validator: pvqLocationModelValidator
        }
      };
    }
    return {
      presence: true, location: { validator: address }
    };
  },
  NatureOfAction: { presence: true, hasValue: true },

  IsCivilCourtActionClosed: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        presence: true, hasValue: { validator: hasYesOrNo }
      };
    }
    return {};
  },

  CivilCourtActionClosedDate: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options?.formType)) {
      const { applicantBirthdate } = options;
      const validationCondition = attributes?.IsCivilCourtActionClosed?.value === 'Yes';
      if (validationCondition) {
        return {
          presence: true,
          date: {
            earliest: applicantBirthdate || DEFAULT_EARLIEST, tooEarly: 'error.date.min', latest: DEFAULT_LATEST, tooLate: 'error.date.max'
          },
        };
      }
    }
    return {};
  },
  OutcomeOfAction: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType) && attributes?.IsCivilCourtActionClosed?.value === 'Yes') {
      return {
        presence: true,
        array: {
          validator: {
            presence: true,
            inclusion: civilCourtOutcomeOptions
          },
          length: { minimum: 1, message: REQUIRED }
        }
      };
    }
    return {};
  },
  OutcomeOtherExplanation: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      if (attributes.OutcomeOfAction?.values?.includes('Other')) {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  ScheduledCourtDate: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options?.formType)) {
      const validationCondition = attributes?.IsCivilCourtActionClosed?.value === 'No';
      if (validationCondition) {
        return {
          presence: true,
          date: { earliest: DEFAULT_LATEST, tooEarly: 'error.date.past', latest: DEFAULT_FUTURE_LATEST },
        };
      }
    }
    return {};
  },
  IsCivilActionAPublicRecord: (value, attributes, attributeName, options) => { // PVQ Only
    if (isPVQForm(options.formType)) {
      return {
        presence: true, hasValue: { validator: hasYesOrNo }
      };
    }
    return {};
  },

  ResultsOfAction: (value, attributes, attributeName, options) => { // SF Only
    if (isPVQForm(options.formType)) {
      return {};
    }
    return { presence: true, hasValue: true };
  },

  PrincipalPartyNames: (value, attributes, attributeName, options) => { // SF only
    if (isPVQForm(options.formType)) {
      return {};
    }
    return { presence: true, hasValue: true };
  },
};

export default legalNonCriminalCourtActionsItemValidator;
